import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { X, Search, UserPlus, RefreshCw, Loader2, Hash, Mail, Phone, Building2, AlertCircle } from 'lucide-react';

/**
 * PersonMasterSearchModal — Stage 1 "Search master first" step for Add Director.
 *
 * Flow:
 *   1. Admin types a query (name / DIN / PAN / email / phone).
 *   2. We show candidate Person Masters from /api/admin/person-masters/search.
 *   3. Options per candidate:
 *      • "Reappoint here" → POST /api/admin/companies/{cid}/directors/reappoint
 *        Blocked server-side if an active duplicate already exists on this
 *        company; we ALSO pre-check via /active-duplicate for a friendly UI.
 *   4. If no match → "Create new director" opens the classic Director form
 *      (parent handles it by calling onCreateNew()).
 *
 * Props:
 *   companyId      — company we're adding the director to
 *   onClose()      — dismiss the modal
 *   onDone()       — call parent's fetchData() after a successful reappointment
 *   onCreateNew()  — parent opens its own DirectorModal in "create fresh" mode
 */
export default function PersonMasterSearchModal({ companyId, onClose, onDone, onCreateNew }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reappointing, setReappointing] = useState(null); // master.id being reappointed
  const [dupCheck, setDupCheck] = useState({}); // {masterId: {has_active_duplicate: bool}}

  const runSearch = async (query) => {
    if (!query || query.trim().length < 2) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get('/admin/person-masters/search', {
        params: { q: query.trim(), limit: 20 },
      });
      setItems(res.data.items || []);
      // Also pre-check active-duplicate for this company for each candidate
      const checks = await Promise.all(
        (res.data.items || []).map(async (m) => {
          try {
            const r = await api.get(
              `/admin/companies/${companyId}/directors/active-duplicate`,
              { params: { person_master_id: m.id } }
            );
            return [m.id, r.data];
          } catch (e) {
            return [m.id, { has_active_duplicate: false }];
          }
        })
      );
      setDupCheck(Object.fromEntries(checks));
    } catch (err) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  // debounced auto-search
  useEffect(() => {
    const t = setTimeout(() => runSearch(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const handleReappoint = async (master) => {
    setReappointing(master.id);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await api.post(`/admin/companies/${companyId}/directors/reappoint`, {
        person_master_id: master.id,
        appointment_date: today,
        designation: 'Director',
      });
      toast.success(`Reappointed ${master.full_name}`);
      onDone && onDone();
      onClose && onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Reappointment failed';
      toast.error(detail);
    } finally {
      setReappointing(null);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      data-testid="person-master-search-modal"
    >
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Search className="w-5 h-5 text-emerald-400" /> Find or Create Director
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Search the Person Master first to avoid duplicates. Reappoint an existing person or create a new one.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white"
            data-testid="person-master-search-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 border-b border-slate-700">
          <label className="text-xs text-slate-400 block mb-1">Search by name, DIN, PAN, email, or phone</label>
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Start typing…"
            className="bg-slate-900 border-slate-700 text-white text-sm"
            data-testid="person-master-search-input"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loading && (
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Searching…
            </div>
          )}

          {!loading && q.trim().length >= 2 && items.length === 0 && (
            <div className="text-center py-6 border-2 border-dashed border-slate-700/50 rounded-lg">
              <AlertCircle className="w-6 h-6 text-slate-500 mx-auto mb-2" />
              <p className="text-slate-400 text-sm">No matching Person Master found.</p>
              <p className="text-slate-500 text-xs">Create a fresh director below.</p>
            </div>
          )}

          {items.map((m) => {
            const dup = dupCheck[m.id]?.has_active_duplicate;
            return (
              <div
                key={m.id}
                className="bg-slate-900/60 border border-slate-700/50 rounded-lg p-4"
                data-testid={`person-master-candidate-${m.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-white font-medium">{m.full_name}</span>
                      {m.din && (
                        <span className="text-xs bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded font-mono">
                          DIN: {m.din}
                        </span>
                      )}
                      {m.pan && (
                        <span className="text-xs bg-purple-500/20 text-purple-300 px-1.5 py-0.5 rounded font-mono">
                          PAN: {m.pan}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-300">
                      {m.email && (
                        <div className="flex items-center gap-1.5 truncate">
                          <Mail className="w-3 h-3 text-slate-500" /> {m.email}
                        </div>
                      )}
                      {m.phone && (
                        <div className="flex items-center gap-1.5">
                          <Phone className="w-3 h-3 text-slate-500" /> {m.phone}
                        </div>
                      )}
                      <div className="flex items-center gap-1.5">
                        <Building2 className="w-3 h-3 text-slate-500" /> {m.linked_companies_count || 0} companies
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Hash className="w-3 h-3 text-slate-500" /> {m.linked_directors_count || 0} appointments
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <Button
                      size="sm"
                      disabled={dup || reappointing === m.id}
                      onClick={() => handleReappoint(m)}
                      className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
                      data-testid={`reappoint-btn-${m.id}`}
                    >
                      {reappointing === m.id ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3.5 h-3.5 mr-1" />
                      )}
                      {dup ? 'Already active here' : 'Reappoint on this company'}
                    </Button>
                  </div>
                </div>
                {dup && (
                  <p className="mt-2 text-xs text-amber-400 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> An active appointment already exists on this company. Cease it before reappointing.
                  </p>
                )}
              </div>
            );
          })}

          {q.trim().length < 2 && (
            <p className="text-xs text-slate-500 italic text-center py-6">
              Type at least 2 characters to search…
            </p>
          )}
        </div>

        <div className="p-5 border-t border-slate-700 flex items-center justify-between gap-3 bg-slate-900/40">
          <p className="text-xs text-slate-400">Nobody matched?</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button
              onClick={() => {
                onClose && onClose();
                onCreateNew && onCreateNew();
              }}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="create-new-director-btn"
            >
              <UserPlus className="w-4 h-4 mr-1" /> Create new director
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
